import type { FormProps } from '../../types'
import { Plus } from 'lucide-react'
import React, { useState } from 'react'
import { useController } from 'react-hook-form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select'

export type SelectCreatableOption = {
  label: string
  value: string
  data?: unknown
  disabled?: boolean
  default?: boolean
}

export interface CustomShadcnSelect {
  name: string
  type: string
  customControlType: 'shadcn-select'
  value?: string | number
  options: SelectCreatableOption[]
  componentOptions?: {
    showEmpty?: number
  }
  creatable?: boolean
  listen?: {
    on: string
    execute: (
      setter: Record<string, unknown>,
    ) => (newValue: string | number) => void
  }
  hideSeparator?: boolean
  menuPlacement?: 'top' | 'bottom'
  validation?: {
    required?: boolean
    minLength?: number
    maxLength?: number
    pattern?: string
    min?: number
    max?: number
    step?: number
  }
  uiOptions?: {
    placeholder?: string
    disabled?: boolean
    testId?: string
  }
}

export type CustomSelectProps = {
  field: CustomShadcnSelect
  formProps: FormProps
}

export default function CustomShadcnSelect({
  field,
  formProps,
}: CustomSelectProps) {
  const [options, setOptions] = useState<SelectCreatableOption[]>(field.options)
  const [selectedValue, setSelectedValue] = useState<string | undefined>(
    undefined,
  )

  const CustomOption = (props: { setValue: (value: string) => void }) => {
    const [value, setValue] = useState('')
    const onAdd = () => {
      if (!value) return
      const newValue = {
        label: value,
        value: value,
      }
      setOptions((prevOptions) => [...(prevOptions || []), newValue])
      props.setValue(value)
      setValue('')
    }
    return (
      <div
        className="px-3 py-2 flex w-full items-center space-x-2 border-t border-base-input"
        onKeyDown={(e) => e.stopPropagation()}
      >
        <input
          type="text"
          value={value}
          placeholder="Add new option..."
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              onAdd()
            }
          }}
          className="flex-1 h-8 items-center rounded-md border border-base-input bg-background-light text-slate-50 px-2 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
        <button
          className="inline-flex border border-base-input h-8 items-center justify-center rounded-md px-3 text-xs font-medium text-slate-50 hover:bg-primary/90"
          onClick={() => onAdd()}
        >
          <Plus className="h-3 w-3 mr-1" />
          Add
        </button>
      </div>
    )
  }

  const {
    field: { onChange, value, ref },
  } = useController({
    control: formProps.control,
    name: field.name,
  })

  React.useEffect(() => {
    setSelectedValue(value)
  }, [value])

  const handleValueChange = (newValue: string) => {
    setSelectedValue(newValue)
    onChange(newValue)
  }

  return (
    <Select
      onValueChange={handleValueChange}
      value={value}
      disabled={field.uiOptions?.disabled}
    >
      <SelectTrigger
        ref={ref}
        className="w-full border border-base-input bg-background-light text-slate-50"
      >
        <SelectValue placeholder={field.uiOptions?.placeholder} />
      </SelectTrigger>

      <SelectContent className="max-h-[300px] items-center border border-base-input p-0 overflow-y-auto bg-[var(--color-background-light)] rounded-md shadow-lg">
        <div className=" ">
          {options.map((option) => (
            <SelectItem
              key={option.value}
              value={option.value}
              disabled={option.disabled}
              className={`flex items-center px-3 py-2 cursor-pointer transition-colors ${!option.disabled
                  ? 'hover:!bg-primary hover:!text-white rounded-md'
                  : 'opacity-50 cursor-not-allowed'
                } ${selectedValue === option.value ? 'bg-primary/20 rounded-md' : ''
                }`}
            >
              <div className="flex-1">{option.label}</div>
            </SelectItem>
          ))}
        </div>

        {field.creatable && (
          <CustomOption
            setValue={(newValue) => {
              setOptions([...options, { value: newValue, label: newValue }])
              onChange(newValue)
              setSelectedValue(newValue)
            }}
          />
        )}
      </SelectContent>
    </Select>
  )
}
